/**
 * Campo de senha com botão de revelar.
 *
 * Digitar senha em teclado de celular erra muito, e o erro é invisível por
 * definição. Poder conferir o que se digitou resolve mais do que qualquer
 * mensagem de validação — e é o motivo de o campo de confirmação existir só
 * por decisão de produto, não por necessidade.
 *
 * O botão alterna `type` entre `password` e `text`. Ele fica **fora** da ordem
 * de tabulação: quem navega por teclado passa direto do campo para o próximo,
 * sem esbarrar num controle opcional. Continua alcançável por leitor de tela,
 * que anuncia o rótulo e o estado.
 */

import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PasswordFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  minLength?: number;
  invalid?: boolean;
  describedBy?: string;
}

export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  minLength,
  invalid,
  describedBy,
}: PasswordFieldProps) {
  const { t } = useTranslation(['auth']);
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [visible, setVisible] = useState(false);

  return (
    <div className="mh-field">
      <label htmlFor={fieldId}>{label}</label>
      <div className="relative flex items-center">
        <input
          id={fieldId}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          minLength={minLength}
          required
          value={value}
          aria-invalid={invalid ? 'true' : undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
          className="w-full pr-12"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? t('auth:password.hide') : t('auth:password.show')}
          aria-pressed={visible}
          className="absolute right-1 flex h-10 w-10 items-center justify-center rounded-full text-(--color-sand-500)"
        >
          <EyeIcon crossed={visible} />
        </button>
      </div>
    </div>
  );
}

/** `aria-hidden` porque o significado já está no rótulo do botão. */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {crossed ? <path d="m3 3 18 18" /> : null}
    </svg>
  );
}
